import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { VistaPreviaDocumento } from '../../components/comercial/VistaPreviaDocumento';
import { SignatureModal } from '../../components/comercial/SignatureModal';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, MapPin, Phone, Droplets, Wrench, CalendarCheck,
  Truck, AlertTriangle, Navigation, Play, CheckCircle2, Camera, X, FileText,
  ArrowLeft, Users, PenLine
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_META = {
  prueba: { label: 'Prueba gratuita', icon: Droplets, cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  instalacion: { label: 'Instalación definitiva', icon: Wrench, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  mantenimiento: { label: 'Mantenimiento', icon: CalendarCheck, cls: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
  retirada: { label: 'Retirada', icon: Truck, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  incidencia: { label: 'Incidencia', icon: AlertTriangle, cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const FUENTE_POR_TIPO = { prueba: 'trial', instalacion: 'orden', retirada: 'orden', mantenimiento: 'mantenimiento', incidencia: 'incidencia' };
const COMPLETADOS = ['completada', 'completado', 'resolved'];

const comprimirFoto = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL('image/jpeg', 0.7));
  };
  img.src = URL.createObjectURL(file);
});

function CompletarModal({ trabajo, onClose, onCompletado }) {
  const [nota, setNota] = useState('');
  const [fotos, setFotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const addFotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - fotos.length);
    const comprimidas = await Promise.all(files.map(comprimirFoto));
    setFotos((f) => [...f, ...comprimidas]);
  };

  const completar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/tecnico/trabajos/${FUENTE_POR_TIPO[trabajo.tipo]}/${trabajo.id}/accion`,
        { accion: 'completar', nota, fotos }, { withCredentials: true });
      toast.success('Trabajo completado y sincronizado con la ficha del cliente');
      onCompletado(trabajo);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al completar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="completar-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] font-light text-xl">Completar · {trabajo.cliente}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">Notas del técnico</label>
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)}
              placeholder="Trabajo realizado, observaciones, estado del equipo..."
              className="bg-white/[0.03] border-white/10 text-white min-h-[80px]" data-testid="completar-nota-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">Fotografías (máx. 3)</label>
            <div className="flex gap-2 flex-wrap">
              {fotos.map((f, i) => (
                <div key={i} className="relative">
                  <img src={f} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                  <button onClick={() => setFotos(fotos.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {fotos.length < 3 && (
                <label className="w-16 h-16 rounded-lg border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-white/40" data-testid="completar-foto-input">
                  <Camera className="w-5 h-5 text-zinc-500" />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={addFotos} />
                </label>
              )}
            </div>
          </div>
          <p className="text-[10px] text-zinc-600">
            Al completar se actualizará automáticamente el historial del cliente (visible en Comercial y Administración).
            Después podrás generar y firmar el documento oficial.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-zinc-300">Cancelar</Button>
            <Button onClick={completar} disabled={saving} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30" data-testid="completar-confirmar-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />{saving ? 'Guardando...' : 'Marcar completado'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrabajoCard({ t, onAccion, onCompletar, onDocumento, vistaEquipo }) {
  const meta = TIPO_META[t.tipo];
  const Icon = meta.icon;
  const completado = COMPLETADOS.includes(t.estado);
  const atrasado = t.fecha && t.fecha < new Date().toISOString().slice(0, 10) && !completado;

  return (
    <div className={`rounded-xl border p-4 ${completado ? 'bg-white/[0.01] border-white/[0.04] opacity-60' : 'bg-white/[0.02] border-white/[0.07]'}`} data-testid={`trabajo-${t.tipo}-${t.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2 rounded-lg border flex-shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${meta.cls}`}>{meta.label}</span>
              <span className="px-2 py-0.5 text-[10px] rounded-full border bg-white/[0.04] text-zinc-400 border-white/10" data-testid={`trabajo-estado-${t.id}`}>{t.estado_label}</span>
              {t.hora && <span className="text-xs text-zinc-400 font-mono">{t.hora}</span>}
              {atrasado && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-500/15 text-red-400 border border-red-500/30">Atrasado</span>}
              {!t.fecha && <span className="px-2 py-0.5 text-[10px] rounded-full bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">Por programar</span>}
            </div>
            <p className="text-sm text-white font-medium mt-1.5">{t.cliente} <span className="text-xs text-zinc-600 font-mono">{t.client_code}</span></p>
            {t.equipo && <p className="text-xs text-zinc-500 mt-0.5">Equipo: {t.equipo}</p>}
            {t.nota_origen && <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{t.nota_origen}</p>}
            {vistaEquipo && t.tecnico_name && (
              <p className="text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-1"><Users className="w-3 h-3" />{t.tecnico_name}{t.asignado_a_mi ? ' (tú)' : ''}</p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {t.direccion && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(t.direccion)}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300" data-testid={`trabajo-maps-${t.id}`}>
                  <MapPin className="w-3.5 h-3.5" />{t.direccion}
                </a>
              )}
              {t.telefono && (
                <a href={`tel:${t.telefono}`} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300" data-testid={`trabajo-tel-${t.id}`}>
                  <Phone className="w-3.5 h-3.5" />{t.telefono}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
          {!completado && ['pendiente', 'asignada', 'programado', 'pending'].includes(t.estado) && (
            <Button size="sm" onClick={() => onAccion(t, 'en_camino')} className="bg-white/[0.06] text-zinc-200 border border-white/15 h-8 hover:bg-white/[0.12]" data-testid={`trabajo-encamino-${t.id}`}>
              <Navigation className="w-3.5 h-3.5 mr-1" />En camino
            </Button>
          )}
          {!completado && ['pendiente', 'asignada', 'programado', 'pending', 'en_camino'].includes(t.estado) && (
            <Button size="sm" onClick={() => onAccion(t, 'iniciar')} className="bg-sky-500/20 text-sky-400 border border-sky-500/30 h-8" data-testid={`trabajo-iniciar-${t.id}`}>
              <Play className="w-3.5 h-3.5 mr-1" />Iniciar
            </Button>
          )}
          {!completado && ['en_progreso', 'in_progress', 'en_camino'].includes(t.estado) && (
            <Button size="sm" onClick={() => onCompletar(t)} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 h-8" data-testid={`trabajo-completar-${t.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Completar
            </Button>
          )}
          {completado && t.trial_id && (
            <Button size="sm" variant="outline" onClick={() => onDocumento(t)} className="border-amber-500/30 text-amber-400 h-8" data-testid={`trabajo-documento-${t.id}`}>
              <FileText className="w-3.5 h-3.5 mr-1" />Documento
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AgendaTecnico() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [completando, setCompletando] = useState(null);
  const [docFlow, setDocFlow] = useState(null); // {trabajo, paso: 'preview'|'firmar', documento}

  const fetchAgenda = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tecnico/agenda`, { params: { fecha }, withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error('Error cargando la agenda');
    }
  }, [fecha]);

  useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

  const moverDia = (dias) => {
    const d = new Date(fecha);
    d.setDate(d.getDate() + dias);
    setFecha(d.toISOString().slice(0, 10));
  };

  const accion = async (t, acc) => {
    try {
      await axios.post(`${API_URL}/api/tecnico/trabajos/${FUENTE_POR_TIPO[t.tipo]}/${t.id}/accion`,
        { accion: acc }, { withCredentials: true });
      toast.success(acc === 'en_camino' ? 'Marcado en camino' : 'Trabajo iniciado');
      fetchAgenda();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const abrirDocumento = (t) => setDocFlow({ trabajo: t, paso: 'preview' });

  const onDocumentoGenerado = async () => {
    // Buscar el documento recién generado (pendiente de firma) para firmarlo in situ
    const t = docFlow.trabajo;
    try {
      const r = await axios.get(`${API_URL}/api/documentos`, { params: { trial_id: t.trial_id }, withCredentials: true });
      const doc = (r.data.documentos || []).find((d) => d.estado === 'pendiente_firma');
      if (doc) {
        await axios.post(`${API_URL}/api/tecnico/trabajos/${FUENTE_POR_TIPO[t.tipo]}/${t.id}/documento`,
          { documento_id: doc.id }, { withCredentials: true });
        setDocFlow({ trabajo: t, paso: 'firmar', documento: doc });
      } else {
        setDocFlow(null);
      }
    } catch { setDocFlow(null); }
  };

  const firmarDocumento = async (payload) => {
    const r = await axios.post(`${API_URL}/api/documentos/${docFlow.documento.id}/firmar`, payload, { withCredentials: true });
    toast.success(r.data.estado === 'firmado' ? 'Documento firmado · trabajo cerrado' : 'Firma registrada');
  };

  const trabajos = data?.trabajos || [];
  const fechaLabel = new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="agenda-tecnico-page">
      <header className="border-b border-white/[0.06] px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 bg-[#030303]/95 backdrop-blur z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/tecnico')} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.06]" data-testid="agenda-volver-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg font-light text-white font-['Outfit']">Agenda Técnica</h1>
            <p className="text-[10px] text-zinc-500">
              {data?.vista_equipo ? 'Vista de equipo · todos los trabajos' : `Trabajos asignados a ${user?.name || 'ti'}`}
            </p>
          </div>
        </div>
        <img src="/manklar-logo-white.png" alt="Manklar" className="h-7 hidden sm:block" />
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => moverDia(-1)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.06]" data-testid="agenda-dia-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="flex items-center gap-3">
            <p className="text-sm text-white capitalize">{fechaLabel}</p>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              className="w-36 h-8 bg-white/[0.03] border-white/10 text-white text-xs" data-testid="agenda-fecha-input" />
            {fecha !== new Date().toISOString().slice(0, 10) && (
              <button onClick={() => setFecha(new Date().toISOString().slice(0, 10))} className="text-xs text-sky-400 hover:text-sky-300" data-testid="agenda-hoy-btn">Hoy</button>
            )}
          </div>
          <button onClick={() => moverDia(1)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.06]" data-testid="agenda-dia-next"><ChevronRight className="w-4 h-4" /></button>
        </div>

        {data && (
          <div className="flex gap-2 flex-wrap" data-testid="agenda-resumen">
            <span className="px-3 py-1.5 rounded-full text-xs bg-white/[0.04] border border-white/10 text-zinc-300">
              {data.pendientes} pendiente(s) de {trabajos.length}
            </span>
            {Object.entries(data.resumen).map(([tipo, n]) => (
              <span key={tipo} className={`px-3 py-1.5 rounded-full text-xs border ${TIPO_META[tipo]?.cls}`}>
                {n} {TIPO_META[tipo]?.label.toLowerCase()}{n > 1 ? 's' : ''}
              </span>
            ))}
          </div>
        )}

        {!data ? (
          <p className="text-sm text-zinc-500 py-10 text-center">Cargando agenda...</p>
        ) : trabajos.length === 0 ? (
          <div className="text-center py-14">
            <CalendarCheck className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">Sin trabajos para este día</p>
          </div>
        ) : (
          <div className="space-y-3">
            {trabajos.map((t) => (
              <TrabajoCard key={`${t.tipo}-${t.id}`} t={t} vistaEquipo={data.vista_equipo}
                onAccion={accion} onCompletar={setCompletando} onDocumento={abrirDocumento} />
            ))}
          </div>
        )}
      </main>

      {completando && (
        <CompletarModal
          trabajo={completando}
          onClose={() => setCompletando(null)}
          onCompletado={(t) => { fetchAgenda(); if (t.trial_id) setDocFlow({ trabajo: t, paso: 'preview' }); }}
        />
      )}
      {docFlow?.paso === 'preview' && (
        <VistaPreviaDocumento
          trialId={docFlow.trabajo.trial_id}
          templateCodigo={docFlow.trabajo.documento_sugerido}
          onClose={() => setDocFlow(null)}
          onSaved={onDocumentoGenerado}
        />
      )}
      {docFlow?.paso === 'firmar' && (
        <SignatureModal
          documento={docFlow.documento}
          onClose={() => { setDocFlow(null); fetchAgenda(); }}
          onSign={firmarDocumento}
        />
      )}
    </div>
  );
}
